import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { useGetImproveScoreDetailV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { getImageUrl } from '@utils/nutrition/getImageUrl';
import { getFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';
import {
  ImproveThisActivitySheet,
  ImproveThisActivitySheetSkeleton,
} from './ImproveThisActivitySheet';
import { KEYS, LocalStorage } from '@utils/LocalStorage';
import {
  useGetSubscriptionStatusQuery
} from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { SubscriptionStatus } from '@viome/viome-base/src/usecase/Misc/MiscModel';
import { Button } from '@design-system/Button';
import { FORMULA_PATH, SHOP_PATH } from '@/constants/paths';
import { useShowFormulaRecs } from '@/hooks/useShowFormulaRecs';

type ItemType = 'Foods' | 'Ingredients' | '';

interface FoodAndIngredientsListProps {
  scoreId?: number | null;
  reportType: string;
  conditionName: string;
  isDetected: boolean;
  onDataAvailability?: (hasData: boolean) => void;
}

export const FoodAndIngredientsList: React.FC<FoodAndIngredientsListProps> = ({
  scoreId,
  reportType,
  conditionName,
  isDetected,
  onDataAvailability,
}) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState<ItemType>('');
  const [apiError, setApiError] = useState<boolean>(false);

  const {
    data: improveScoreDetailData,
    isLoading,
    error,
  } = useGetImproveScoreDetailV2(
    'L3',
    isDetected
      ? { score_id: scoreId, report_type: reportType }
      : { report_type: reportType, condition_name: conditionName },
  );
  const { data: subscriptionData } = useGetSubscriptionStatusQuery();

  // active subscription excluding the test-kit sub
  const hasActiveSubscription = subscriptionData?.subscriptions?.some(
    (subscription) =>
      (subscription.category ?? '').toUpperCase() !== 'KIT' &&
      subscription.subscription_status === SubscriptionStatus.Active,
  );
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isEligibleForSupplements } = localStorageUser && JSON.parse(localStorageUser);
  const showFormulaRecs = useShowFormulaRecs();

  const { items: improveScoreList = [] } = improveScoreDetailData || {};
  const [categoryValueList, setCategoryValueList] = useState(improveScoreList);

  useEffect(() => {
    if (isLoading && onDataAvailability) {
      onDataAvailability(true);
    }
  }, []);

  useEffect(() => {
    if (improveScoreList) {
      setCategoryValueList(improveScoreList);
    }
  }, [improveScoreList]);

  useEffect(() => {
    const hasError = !!error || !improveScoreDetailData;
    setApiError(hasError);

    if (!isLoading) {
      const hasData =
        !hasError &&
        (!!improveScoreDetailData?.goal ||
          (improveScoreList && improveScoreList.length > 0));
      onDataAvailability?.(hasData);
    }
  }, [
    error,
    improveScoreDetailData,
    improveScoreList,
    isLoading,
    onDataAvailability,
  ]);

  const getScoreImageUrl = (item_type: string, item_url: string) => {
    return item_type === 'Foods'
      ? getImageUrl(item_url)
      : getFormulaImageUrl(item_url);
  };

  const onChangeCategory = (categoryType: ItemType) => {
    if (categoryType === '') {
      setCategoryValueList(improveScoreList);
      setCategory('');
    } else {
      const filteredList = improveScoreList?.filter(
        (item) => item.item_type === categoryType,
      );
      setCategory(categoryType);
      setCategoryValueList(filteredList);
    }
  };

  const onClickNavigate = (itemType: ItemType, item_id: number) => {
    if (!itemType) return;
    const redirectionRoute = itemType === 'Foods' ? 'foods' : 'ingredients';
    navigate(`/nutrition/${redirectionRoute}/${item_id}`);
  };

  const FoodAndIngredientsListSkeleton = () => (
    <div className="flex flex-col animate-pulse">
      <div className="h-8 w-64 bg-gray-200 rounded mb-4"></div>
      <div className="h-4 w-48 bg-gray-200 rounded mb-6"></div>
      <div className="h-10 w-full bg-gray-200 rounded mb-6"></div>
      <div className="space-y-2">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="h-20 w-full bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  );

  const hasGoalData = improveScoreDetailData?.goal;
  const showFoodAndIngredients = improveScoreList.length > 0;
  const foodsCount = showFoodAndIngredients
    ? improveScoreList.filter((item) => item.item_type === 'Foods').length
    : 0;
  const ingredientsCount = showFoodAndIngredients
    ? improveScoreList.filter((item) => item.item_type === 'Ingredients').length
    : 0;

  if (apiError && !isLoading && !hasGoalData && !showFoodAndIngredients) {
    return null;
  }

  const getEmptyStateMessage = () => {
    if (category === 'Foods') {
      return 'No foods to support this activity';
    } else if (category === 'Ingredients') {
      return 'No ingredients to support this activity';
    } else {
      return 'No foods and ingredients to support this activity';
    }
  };

  const handleButtonClick = () => {
    if (hasActiveSubscription) {
      navigate(FORMULA_PATH);
    } else {
      navigate(SHOP_PATH);
    }
  };

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <>
          <ImproveThisActivitySheetSkeleton />
          <div className="mt-8">
            <FoodAndIngredientsListSkeleton />
          </div>
        </>
      ) : (
        <>
          {hasGoalData && (
            <ImproveThisActivitySheet title={improveScoreDetailData?.goal} />
          )}

          {showFoodAndIngredients ? (
            <>
              <div className="flex flex-col gap-4">
                <h1 className="typography-h2 text-primary mt-8">
                  Foods & ingredients to support this activity
                </h1>
                <p className="text-sm text-gray-600">
                  {foodsCount} foods & {ingredientsCount} ingredients
                </p>
              </div>

              <div className="mt-4">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  onValueChange={onChangeCategory}
                  value={category}
                >
                  <ToggleGroupItem
                    className="px-4 py-2 bg-white typography-btn-medium"
                    value="Foods"
                  >
                    Foods
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    className="px-4 py-2 bg-white typography-btn-medium"
                    value="Ingredients"
                  >
                    Ingredients
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <div className="mt-6 pr-2">
                <div className="space-y-2">
                  {categoryValueList?.length > 0 ? (
                    <>
                      {categoryValueList?.map((list) => {
                        const {
                          item_id,
                          item_tag,
                          item_title,
                          item_type,
                          item_url,
                        } = list || {};
                        return (
                          <button
                            onClick={() => onClickNavigate(item_type, item_id)}
                            className="w-full h-20 px-6 py-1 pr-5 rounded-md bg-white flex justify-between items-center text-left"
                            key={`${item_id}-${item_title}`}
                          >
                            <div className="flex items-center gap-7">
                              <div className="h-16 flex items-center justify-center">
                                <img
                                  src={getScoreImageUrl(item_type, item_url)}
                                  alt={item_title}
                                  className="w-10 h-10"
                                />
                              </div>

                              <div className="flex flex-col line-clamp-1">
                                <h3 className="typography-body-emphasis text-primary">
                                  {item_title}
                                </h3>
                                <p className="typography-caption1 text-primary/70">
                                  {item_tag}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </>
                  ) : (
                    <div className="py-8 text-center text-gray-500">
                      {getEmptyStateMessage()}
                    </div>
                  )}
                </div>
              </div>

              {(isEligibleForSupplements && ingredientsCount > 0 && showFormulaRecs) && (
                <div className="sticky bottom-8 left-0 w-full bg-black rounded-lg">
                  <Button
                    className="w-full rounded-full h-14 text-white"
                    onClick={handleButtonClick}
                  >
                    {hasActiveSubscription ? 'See My Formulas' : 'Shop My Formulas'}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
};
